import {
  BUDGET_CHANGE_TYPE,
  BUDGET_FAMILY,
  DEFAULT_GUARD_RAILS,
  defaultChangeTypeForBudgetFamily,
  isAllocateChangeType,
  isRestOfAllocatedBudgetType,
} from "./constants";
import {
  buildCubeFiltersFromFilterModule,
  filterModuleHasContent,
  hydrateFilterModuleFromCubeFilters,
  normalizeCubeFilters,
} from "./cubeFilters";
import { timePeriodToCustomDate } from "../../../helper/Quadrant/quadrantHelper";
import {
  formatIsoDateForInput,
  frequencyValuesFromDayIndices,
  generateId,
  getCampaignBaseBudget,
  getMinCampaignBudget,
  getRestOfAllocatedPercent,
  isCampaignRestLockedInSlot,
  parseNumOrEmpty,
  selectedDayIndicesFromFrequency,
} from "./helpers";

export function createDefaultCampaignBudget(campaign, { budgetFamily = BUDGET_FAMILY.SET } = {}) {
  const base = getCampaignBaseBudget(campaign);
  return {
    status: "enabled",
    base_value: base,
    min_value: getMinCampaignBudget(),
    max_value: budgetFamily === BUDGET_FAMILY.ALLOCATE ? base : "",
  };
}

export function createDefaultSlotCampaignBudget(
  _multiCampaign,
  budgetFamily = BUDGET_FAMILY.SET
) {
  return {
    change_type: defaultChangeTypeForBudgetFamily(budgetFamily),
    hasCustomType: false,
    change_value: "",
    hasCustomValue: false,
  };
}

export function createRestLockedSlotCampaignBudget() {
  return {
    change_type: BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET,
    hasCustomType: true,
    change_value: 0,
    hasCustomValue: true,
  };
}

export function resetCampaignBudgetsOnLaterSlots(
  slots = [],
  fromSlotLocalId,
  mongoIds,
  budgetFamily = BUDGET_FAMILY.SET
) {
  const ids = Array.isArray(mongoIds) ? mongoIds : [mongoIds];
  const fromIndex = slots.findIndex((slot) => slot.localId === fromSlotLocalId);
  if (fromIndex < 0 || !ids.length) return slots;
  return slots.map((slot, index) => {
    if (index <= fromIndex) return slot;
    const campaignBudgets = { ...slot.campaignBudgets };
    ids.forEach((mongoId) => {
      campaignBudgets[mongoId] = createDefaultSlotCampaignBudget(true, budgetFamily);
    });
    return { ...slot, campaignBudgets };
  });
}

export function createDefaultSlot(
  multiCampaign = false,
  { selectedCampaignIds = [], budgetFamily = BUDGET_FAMILY.SET } = {}
) {
  const campaignBudgets = {};
  selectedCampaignIds.forEach((mongoId) => {
    campaignBudgets[mongoId] = createDefaultSlotCampaignBudget(multiCampaign, budgetFamily);
  });
  return {
    localId: generateId(),
    durationId: `dur-${generateId()}`,
    status: "active",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    enableCompounding: false,
    perfFilter: {
      enabled: false,
      timePeriod: "LAST_7_DAY",
      customDate: "last_7d",
      cubeFilters: "",
      filterModuleFilters: null,
    },
    campaignBudgets,
  };
}

export function createInitialFormState() {
  return {
    ruleName: "",
    status: "Active",
    activeCampaignFilter: "All",
    selectedCampaignIds: [],
    campaignMeta: {},
    campaignTableBudgets: {},
    automationMode: "ALL_DAYS_CUSTOM_DAYS",
    budgetFamily: BUDGET_FAMILY.SET,
    scheduleStartDate: "",
    scheduleEndDate: "",
    selectedDays: [0, 1, 2, 3, 4, 5, 6],
    gapHandling: "USE_DEFAULT_BUDGET",
    slots: [createDefaultSlot(false)],
  };
}

function resetSlotCampaignBudgets(prevForm, budgetFamily) {
  const multiCampaign = (prevForm?.selectedCampaignIds ?? []).length > 1;
  return (prevForm?.slots ?? []).map((slot) => {
    const campaignBudgets = {};
    (prevForm?.selectedCampaignIds ?? []).forEach((mongoId) => {
      campaignBudgets[mongoId] = createDefaultSlotCampaignBudget(multiCampaign, budgetFamily);
    });
    return {
      ...slot,
      campaignBudgets,
      ...(budgetFamily === BUDGET_FAMILY.ALLOCATE
        ? {
            status: "active",
            perfFilter: {
              enabled: false,
              timePeriod: "LAST_7_DAY",
              customDate: "last_7d",
              cubeFilters: "",
              filterModuleFilters: null,
            },
          }
        : {}),
    };
  });
}

export function applyAllocateMinMaxToTableBudgets(campaignTableBudgets = {}, selectedCampaignIds = []) {
  const next = { ...campaignTableBudgets };
  selectedCampaignIds.forEach((mongoId) => {
    const current = next[mongoId] ?? createDefaultCampaignBudget({ daily_budget: 0 });
    next[mongoId] = {
      ...current,
      min_value: getMinCampaignBudget(),
      max_value: current.base_value,
    };
  });
  return next;
}

/** Reset slot/day fields when switching between All Days and Dayparting. */
export function buildAutomationModeResetPatch(prevForm, automationMode) {
  const selectedCampaignIds = prevForm?.selectedCampaignIds ?? [];
  const multiCampaign = selectedCampaignIds.length > 1;
  const slot = createDefaultSlot(multiCampaign, {
    selectedCampaignIds,
    budgetFamily: BUDGET_FAMILY.SET,
  });

  return {
    automationMode,
    budgetFamily: BUDGET_FAMILY.SET,
    selectedDays: [0, 1, 2, 3, 4, 5, 6],
    slots: [slot],
  };
}

/** Reset slot budget types (and ALLOCATE min/max) when switching Set / Allocate. */
export function buildBudgetFamilyResetPatch(prevForm, budgetFamily) {
  const selectedCampaignIds = prevForm?.selectedCampaignIds ?? [];
  const nextPatch = {
    budgetFamily,
    slots: resetSlotCampaignBudgets(prevForm, budgetFamily),
  };

  if (budgetFamily === BUDGET_FAMILY.ALLOCATE) {
    nextPatch.gapHandling = "PAUSE_CAMPAIGN";
    nextPatch.campaignTableBudgets = applyAllocateMinMaxToTableBudgets(
      prevForm?.campaignTableBudgets,
      selectedCampaignIds
    );
  }

  return nextPatch;
}

function buildScheduleConfigForForm(form) {
  const freqValues = frequencyValuesFromDayIndices(form.selectedDays);

  return {
    type: null,
    day_frequency: freqValues ? "CUSTOM_DAYS" : null,
    frequency_values: freqValues,
    start_hour: null,
    end_hour: null,
    start_date: form.scheduleStartDate || null,
    end_date: form.scheduleEndDate || null,
    timezone: null,
  };
}

function buildDurationConfigForSlot(form, slot) {
  const isDayparting = form.automationMode === "DAYPARTING";
  const slotStatus =
    form.budgetFamily === BUDGET_FAMILY.ALLOCATE ? "active" : slot.status || "active";

  if (isDayparting) {
    return {
      id: slot.durationId || `dur-${generateId()}`,
      status: slotStatus,
      start_date: form.scheduleStartDate || null,
      end_date: form.scheduleEndDate || null,
      start_time: slot.startTime || "09:00",
      end_time: slot.endTime || "18:00",
    };
  }

  return {
    id: slot.durationId || `dur-${generateId()}`,
    status: slotStatus,
    start_date: slot.startDate || form.scheduleStartDate || null,
    end_date: slot.endDate || form.scheduleEndDate || null,
    start_time: null,
    end_time: null,
  };
}

function buildEntityMetricFilter(slot, platformIds, userPlatform, { filtersDisabled = false } = {}) {
  const shouldBuildCube =
    !filtersDisabled &&
    (slot.perfFilter?.enabled ||
      filterModuleHasContent(slot.perfFilter?.filterModuleFilters) ||
      Boolean(slot.perfFilter?.cubeFilters?.trim()));

  const cube = shouldBuildCube
    ? slot.perfFilter.cubeFilters?.trim() ||
      buildCubeFiltersFromFilterModule(slot.perfFilter?.filterModuleFilters, {
        timePeriod: slot.perfFilter?.timePeriod,
        platformCampaignIds: platformIds,
        userPlatform,
      })
    : "";

  return {
    type: "CUBE_BASED_NESTED_AND_OR",
    time_period: slot.perfFilter.timePeriod || "LAST_7_DAY",
    cube_filters: normalizeCubeFilters(cube),
    enable_interval_compounding: Boolean(slot.enableCompounding),
  };
}

function buildEntityConfigsForTask(form, slot) {
  return form.selectedCampaignIds.map((mongoId) => {
    const meta = form.campaignMeta[mongoId] ?? {};
    const table = form.campaignTableBudgets[mongoId] ?? {};
    const slotRow = slot.campaignBudgets[mongoId] ?? {};
    const restLocked = isCampaignRestLockedInSlot(form, slot, mongoId);
    const changeType = restLocked
      ? BUDGET_CHANGE_TYPE.PERCENT_OF_ALLOCATED_BUDGET
      : slotRow.change_type ?? defaultChangeTypeForBudgetFamily(form.budgetFamily);
    const changeVal = restLocked
      ? 0
      : isRestOfAllocatedBudgetType(changeType)
        ? getRestOfAllocatedPercent(form, mongoId)
        : parseNumOrEmpty(slotRow.change_value);

    const baseVal = parseNumOrEmpty(table.base_value ?? meta.defaultBudget) || 0;
    const minVal = parseNumOrEmpty(table.min_value);
    const maxVal = parseNumOrEmpty(table.max_value);

    return {
      entity_type: "campaign",
      id: mongoId,
      value_constraints: {
        metric: "BUDGET",
        base_value: typeof baseVal === "number" ? baseVal : Number(baseVal) || 0,
        ...(minVal !== "" && { min_value: Number(minVal) }),
        ...(maxVal !== "" && { max_value: Number(maxVal) }),
      },
      change_config: {
        change_type: changeType,
        change_value:
          changeVal === "" ? 0 : typeof changeVal === "number" ? changeVal : Number(changeVal),
      },
      child_entities: null,
    };
  });
}

export function formToApiRule(form, { clientId, email, ruleId, createdAt, userPlatform = "amazon" }) {
  const now = new Date().toISOString();
  const name = form.ruleName.trim();
  const platformIds = form.selectedCampaignIds
    .map((mid) => form.campaignMeta[mid]?.platformId)
    .filter(Boolean);

  const schedule_config = buildScheduleConfigForForm(form);

  const rule_tasks = form.slots.map((slot, index) => {
    const duration = buildDurationConfigForSlot(form, slot);

    const emf = buildEntityMetricFilter(slot, platformIds, userPlatform, {
      filtersDisabled: form.budgetFamily === BUDGET_FAMILY.ALLOCATE,
    });

    return {
      rule_task_name: `Duration ${index + 1}`,
      entity_metric_filter: emf,
      entity_configs: buildEntityConfigsForTask(form, slot),
      rule_action_config: {
        entity_action_config: {
          action_type: "UPDATE_BUDGET",
          apply_decision_flag: null,
          percentage_change: null,
          threshold: null,
          entity_lock_duration_days: null,
          entity_type_wise_percentage_change_and_threshold: null,
          duration_config: duration,
        },
        max_percent_exclude: 50,
      },
    };
  });

  return {
    ...(ruleId ? { id: ruleId } : {}),
    status: form.status || "Active",
    client_id: clientId,
    entity_filter: null,
    rule_tasks,
    schedule_config,
    name,
    type: "AMAZON_CAMPAIGN_ABO",
    modified_by: email || "local@user",
    apply_decision: null,
    source: "amazon",
    created_at: createdAt || now,
    updated_at: now,
    rule_objective: "Campaign_ABO",
    rule_config: {
      automation_mode: form.automationMode,
      budget_family:
        form.automationMode === "DAYPARTING"
          ? form.budgetFamily || BUDGET_FAMILY.SET
          : BUDGET_FAMILY.SET,
      gap_handling:
        form.automationMode === "DAYPARTING" && form.budgetFamily === BUDGET_FAMILY.ALLOCATE
          ? "PAUSE_CAMPAIGN"
          : form.gapHandling,
      guardrails: { ...DEFAULT_GUARD_RAILS },
    },
  };
}

function stripClientGeneratedIdsForCreate(ruleTasks = []) {
  return ruleTasks.map((task) => {
    const duration =
      task?.rule_action_config?.entity_action_config?.duration_config;
    if (!duration?.id) return task;

    const { id, ...durationWithoutId } = duration;
    return {
      ...task,
      rule_action_config: {
        ...task.rule_action_config,
        entity_action_config: {
          ...task.rule_action_config.entity_action_config,
          duration_config: durationWithoutId,
        },
      },
    };
  });
}

/** Request body for POST/PATCH automation-rule-set (matches backend contract). */
export function formToApiRequestBody(form, { email, ruleId, createdAt, clientId, userPlatform }) {
  const rule = formToApiRule(form, { clientId, email, ruleId, createdAt, userPlatform });
  const isCreate = !ruleId;

  const body = {
    type: rule.type,
    name: rule.name,
    status: rule.status,
    rule_objective: rule.rule_objective,
    source: rule.source,
    modified_by: rule.modified_by,
    client_id: rule.client_id,
    apply_decision: rule.apply_decision,
    entity_filter: rule.entity_filter,
    rule_config: rule.rule_config,
    schedule_config: rule.schedule_config,
    rule_tasks: isCreate
      ? stripClientGeneratedIdsForCreate(rule.rule_tasks)
      : rule.rule_tasks,
  };

  if (!isCreate && rule.id) {
    body.id = rule.id;
  }

  return body;
}

export function apiRuleToForm(rule) {
  if (!rule) return createInitialFormState();

  const form = createInitialFormState();
  form.ruleName = rule.name ?? "";
  form.status = rule.status ?? "Active";
  form.automationMode =
    rule.rule_config?.automation_mode ??
    (rule.rule_tasks?.[0]?.rule_action_config?.entity_action_config?.duration_config?.start_time
      ? "DAYPARTING"
      : "ALL_DAYS_CUSTOM_DAYS");
  const inferredAllocate = (rule.rule_tasks ?? []).some((task) =>
    (task.entity_configs ?? []).some((ec) => isAllocateChangeType(ec?.change_config?.change_type))
  );
  form.budgetFamily =
    form.automationMode === "DAYPARTING"
      ? rule.rule_config?.budget_family ??
        (inferredAllocate ? BUDGET_FAMILY.ALLOCATE : BUDGET_FAMILY.SET)
      : BUDGET_FAMILY.SET;
  form.gapHandling =
    form.budgetFamily === BUDGET_FAMILY.ALLOCATE
      ? "PAUSE_CAMPAIGN"
      : rule.rule_config?.gap_handling ?? "USE_DEFAULT_BUDGET";
  form.scheduleStartDate = formatIsoDateForInput(rule.schedule_config?.start_date);
  form.scheduleEndDate = formatIsoDateForInput(rule.schedule_config?.end_date);
  form.selectedDays = selectedDayIndicesFromFrequency(rule.schedule_config?.frequency_values);
  const campaignIds = [];
  const campaignMeta = {};
  const campaignTableBudgets = {};
  const slots = [];

  (rule.rule_tasks ?? []).forEach((task) => {
    const dc = task.rule_action_config?.entity_action_config?.duration_config ?? {};
    const emf = task.entity_metric_filter;
    const slot = createDefaultSlot(false);
    slot.durationId = dc.id ?? slot.durationId;
    slot.status = dc.status ?? "active";
    slot.startDate = formatIsoDateForInput(dc.start_date);
    slot.endDate = formatIsoDateForInput(dc.end_date);
    slot.startTime = String(dc.start_time ?? "09:00").slice(0, 5);
    slot.endTime = String(dc.end_time ?? "18:00").slice(0, 5);
    if (emf) {
      const cubeFilters = normalizeCubeFilters(emf.cube_filters);
      slot.perfFilter.enabled = Boolean(cubeFilters);
      slot.perfFilter.timePeriod = emf.time_period ?? "LAST_7_DAY";
      slot.perfFilter.customDate = timePeriodToCustomDate(emf.time_period);
      slot.perfFilter.cubeFilters = cubeFilters;
      slot.perfFilter.filterModuleFilters = hydrateFilterModuleFromCubeFilters(cubeFilters);
      slot.enableCompounding = Boolean(emf.enable_interval_compounding);
    }

    const slotCampaignBudgets = {};
    (task.entity_configs ?? []).forEach((ec) => {
      if (ec.entity_type !== "campaign") return;
      const mid = String(ec.id);
      if (!campaignIds.includes(mid)) campaignIds.push(mid);
      const vc = ec.value_constraints ?? {};
      const cc = ec.change_config ?? {};
      campaignTableBudgets[mid] = {
        status: "enabled",
        base_value: vc.base_value ?? "",
        min_value:
          vc.min_value !== "" && vc.min_value != null ? vc.min_value : getMinCampaignBudget(),
        max_value: vc.max_value ?? "",
      };
      slotCampaignBudgets[mid] = {
        change_type: cc.change_type ?? defaultChangeTypeForBudgetFamily(form.budgetFamily),
        hasCustomType: true,
        change_value: cc.change_value ?? "",
        hasCustomValue: true,
      };
      campaignMeta[mid] = {
        ...(campaignMeta[mid] || {}),
        defaultBudget: vc.base_value,
      };
    });
    slot.campaignBudgets = slotCampaignBudgets;
    slots.push(slot);
  });

  form.selectedCampaignIds = campaignIds;
  form.campaignTableBudgets = campaignTableBudgets;
  form.campaignMeta = campaignMeta;
  form.slots = slots.length ? slots : [createDefaultSlot(campaignIds.length > 1)];

  return form;
}
